/**
 * The advisor's scoring settings — the Tier 1 limit, the maximum issue age, and record types to leave
 * out — with a local draft until they save. A save asks the server for a fresh scoring pass, so the
 * list is refreshed after it.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { prospectingApi, queryKeys } from '@client/api/integrations';
import type { Prospecting } from '@client/api/types';

export function useScoringSettings(provider: Prospecting.Provider, enabled: boolean) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Prospecting.ScoringSettingsUpdate>({});

  const query = useQuery({
    queryKey: queryKeys.prospecting.scoringSettings(provider),
    queryFn: () => prospectingApi.scoring.settings(provider),
    enabled,
  });

  const save = useMutation({
    mutationFn: (settings: Prospecting.ScoringSettingsUpdate) =>
      prospectingApi.scoring.saveSettings(provider, settings),
    onSuccess: saved => {
      queryClient.setQueryData(queryKeys.prospecting.scoringSettings(provider), saved);
      queryClient.invalidateQueries({ queryKey: [...queryKeys.prospecting.all, provider, 'prospects'] });
      setDraft({});
      toast.success('Settings saved. Your contacts will be scored again shortly.');
    },
  });

  const current = query.data?.settings;
  const defaults = query.data?.defaults;
  // A setting cleared in the draft shows its default, which is what saving it will give.
  const value =
    current && defaults
      ? (Object.fromEntries(
          Object.entries(current).map(([key, saved]) => {
            const drafted = draft[key as keyof Prospecting.ScoringSettings];
            return [key, drafted === undefined ? saved : drafted === null ? defaults[key as keyof Prospecting.ScoringSettings] : drafted];
          }),
        ) as unknown as Prospecting.ScoringSettings)
      : undefined;

  return {
    value,
    bounds: query.data?.bounds,
    defaults: query.data?.defaults,
    isLoading: query.isLoading,
    loadError: query.error?.message ?? null,
    reload: () => query.refetch(),
    isDirty: Object.keys(draft).length > 0,
    change: (changes: Prospecting.ScoringSettingsUpdate) => setDraft(previous => ({ ...previous, ...changes })),
    reset: () => setDraft({}),
    save: () => save.mutate(draft),
    isSaving: save.isPending,
    saveError: save.error?.message ?? null,
  };
}
